import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from "react-toastify";
import StudentTemplate from "../../component/StudentTemplate";
import Api from '../../component/Api';
import Select from "react-select";
import CreatableSelect from 'react-select/creatable';


const AcademicCredit = () => {
  // --- STATE MANAGEMENT ---
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [nheqfLevels, setNheqfLevels] = useState([]);
  const [courseCategory, setCourseCategory] = useState([]);
  const [applications, setApplications] = useState([]);
  const [approvedCourses, setApprovedCourses] = useState([]);

  const [courseInputValue, setCourseInputValue] = useState('');



  // Form State
  const [formData, setFormData] = useState({
    courseName: '',
    discipline: '',
    category: '',
    nheqfLevel: '',
    platform: '',
    credits: '',
    gradeScale: '',
    mode: '',
    grade: '',
    gradePoint: '',
    percentage: '',
    abcAvailable: false,
    document: null,
  });


  const { http } = Api();

  const creditOptions = [4, 3, 2, 1];
  const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D'];

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: '#f4f4f4',
      border: '0',
      boxShadow: 'none',
      minHeight: '58px',
      borderRadius: '0.375rem',
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: '0.75rem',
      paddingTop: '0.75rem',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d',
      fontSize: '1rem',
      transform: 'translateY(-50%)',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#212529',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const fetchApplications = async () => {
    try {
      const res = await http.get('/Student/getActiveApplications');
      if (res.data.status) {
        setApplications(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load application history', err);
    }
  };

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const res = await http.get('/Student/getAllDisciplines');
        if (res.data.status) {
          setDisciplines(res.data.data);
          setNheqfLevels(res.data.levels);
          setCourseCategory(res.data.category);
          setApprovedCourses(res.data.approvedCourses);
        }
      } catch (err) {
        console.error('Failed to load disciplines', err);
      }
    };

    fetchDisciplines();
    fetchApplications();
  }, []);

  const disciplineOptions = useMemo(
    () =>
      disciplines.map((d) => ({
        value: d.id,
        label: d.discipline_name,
      })),
    [disciplines]
  );

  const courseOptions = useMemo(() => {
    if (!approvedCourses || approvedCourses.length === 0) return [];

    const uniqueMap = new Map();
    approvedCourses.forEach(app => {
      if (app.course_name && !uniqueMap.has(app.course_name)) {
        uniqueMap.set(app.course_name, {
          value: app.course_name,
          label: app.course_name,
          data: app
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [approvedCourses]);

  const selectedCourseOption =
    courseOptions.find(opt => opt.value === formData.courseName) || null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCourseNameChange = (newValue) => {
    if (!newValue) {
      setFormData(prev => ({
        ...prev,
        courseName: '',
        course_id: '',
      }));
      return;
    }

    const isNew = newValue.__isNew__;
    const appData = newValue.data || {};

    if (isNew) {
      setFormData(prev => ({
        ...prev,
        course_id: null,
        courseName: newValue.label,

        discipline: '',
        category: '',
        nheqfLevel: '',
        platform: '',
        credits: '',
        mode: '',
        gradeScale: '',

        grade: '',
        percentage: '',
        gradePoint: '',
        abcAvailable: false,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        course_id: newValue.value,
        courseName: newValue.label,

        discipline: appData.discipline_id || '',
        category: appData.course_category || '',
        nheqfLevel: appData.course_level || '',
        platform: appData.platform || '',
        credits: appData.acquired_credits
          ? String(appData.acquired_credits)
          : '',
        mode: appData.mode || '',
        gradeScale: appData.grade_scale || '',

        grade: '',
        percentage: '',
        gradePoint: '',
        abcAvailable: false,
      }));
    }
  };


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const mappedFiles = files.map((file) => ({
      file,
      name: file.name,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      url: URL.createObjectURL(file),
    }));

    setDocuments((prev) => [...prev, ...mappedFiles]);
    e.target.value = null;
  };

  const removeFile = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.abcAvailable) {
      toast.error("ABC Credits must be available to submit.");
      return;
    }

    setIsLoading(true);

    try {
      const payload = new FormData();

      payload.append('course_name', formData.courseName);
      payload.append('discipline_id', formData.discipline);
      payload.append('course_category', formData.category);
      payload.append('course_level', formData.nheqfLevel);
      payload.append('platform', formData.platform);
      payload.append('mode', formData.mode);
      payload.append('acquired_credits', formData.credits);
      payload.append('grade_scale', formData.gradeScale);
      payload.append('grade', formData.grade);
      payload.append('grade_point', formData.gradePoint);
      payload.append('percentage', formData.percentage);
      payload.append('ABC_status', formData.abcAvailable ? 1 : 0);

      documents.forEach((doc) => {
        payload.append('documents[]', doc.file);
      });

      const res = await http.post('/Student/submitExternalCredit', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status) {
        toast.success(res.data.message || 'Course details submitted successfully!');
        fetchApplications();

        setFormData({
          courseName: '',
          discipline: '',
          category: '',
          nheqfLevel: '',
          platform: '',
          credits: '',
          gradeScale: '',
          mode: '',
          grade: '',
          gradePoint: '',
          percentage: '',
          abcAvailable: false,
          document: null,
        });
        setDocuments([]);
      } else {
        toast.error(res.data.message || 'Submission failed.');
      }

    } catch (error) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.errors) {
        toast.error("Please check all required fields.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (label) => {
    const s = (label || '').toLowerCase();

    switch (s) {
      case 'bos approved':
        return 'bg-success bg-opacity-10 text-success border-success';

      case 'rejected':
        return 'bg-danger bg-opacity-10 text-danger border-danger';

      case 'application submitted':
        return 'bg-light bg-opacity-10 text-secondary border-secondary';

      case 'sfa approved':
      case 'principal approved':
      case 'university approved':
        return 'bg-info bg-opacity-10 text-info border-info';

      default:
        return 'bg-light bg-opacity-10 text-secondary border-secondary';
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const stats = {
    total: applications.reduce((acc, curr) => acc + parseInt(curr.acquired_credits || 0), 0),
    approved: applications.filter(a => (a.application_status || '').toLowerCase() === 'approved').length,
  };

  const toRoman = (num) => {
    const romans = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
    return romans[num - 1] || num;
  };

  useEffect(() => {
    return () => documents.forEach(doc => URL.revokeObjectURL(doc.url));
  }, [documents]);

  return (
    <StudentTemplate>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid px-md-4 py-3">

        {/* HEADER SECTION */}
        <div className="row align-items-center mb-4 g-3">
          <div className="col-12 col-md-12">
            <h3 className="fw-bold text-dark mb-1 text-center">External Credit Application</h3>
            <p className="text-muted small mb-0 text-center">Register credits earned from External Platforms</p>
          </div>
          {/* <div className="col-12 col-md-6">
            <div className="d-flex gap-3 justify-content-md-end">

              <div className="card border-0 shadow-sm px-4 py-2 d-flex flex-column align-items-center justify-content-center flex-fill flex-md-grow-0">
                <h4 className="mb-0 fw-bold text-primary">{stats.total}</h4>
                <small className="text-muted fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>CREDITS</small>
              </div>

              <div className="card border-0 shadow-sm px-4 py-2 d-flex flex-column align-items-center justify-content-center flex-fill flex-md-grow-0">
                <h4 className="mb-0 fw-bold text-success">{stats.approved}</h4>
                <small className="text-muted fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>APPROVED</small>
              </div>

            </div>
          </div> */}
        </div>

        <div className="row g-4">

          {/* SECTION 1: FORM (Full Width) */}
          <div className="col-12">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                <h5 className="fw-bold mb-0 text-primary">
                  <i className="fas fa-plus-circle me-2"></i>New Application
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>

                  {/* Course Name - Creatable Select */}
                  <div className="mb-3">
                    <label className="small text-dark fw-bold mb-2 d-block">COURSE NAME *</label>
                    <CreatableSelect
                      isClearable
                      options={courseOptions}
                      value={selectedCourseOption}
                      onChange={handleCourseNameChange}
                      placeholder="Select existing or type to add new..."
                      formatCreateLabel={(inputValue) => `Add New Course: "${inputValue}"`}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating mb-3">
                        <select
                          className="form-select bg-light border-0 text-dark"
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select</option>
                          {courseCategory.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.category_code}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="category" className="text-dark">
                          Course Category
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <select
                          className="form-select bg-light border-0 text-dark"
                          id="lvl"
                          name="nheqfLevel"
                          value={formData.nheqfLevel}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select</option>
                          {nheqfLevels.map((lvl) => (
                            <option
                              key={`${lvl.nheqf_level}-${lvl.semester_id}`}
                              value={lvl.nheqf_level}
                            >
                              Semester {toRoman(lvl.semester_id)} - {lvl.nheqf_level}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="lvl" className="text-dark">
                          NHEQF Level
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="small text-dark fw-bold mb-2 d-block">
                      DISCIPLINE
                    </label>
                    <Select
                      options={disciplineOptions}
                      value={disciplineOptions.find(
                        (opt) => opt.value === formData.discipline
                      )}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          discipline: selected ? selected.value : '',
                        }))
                      }
                      placeholder="Select Discipline"
                      isClearable
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: '#f4f4f4',
                          border: '0',
                          boxShadow: 'none',
                          minHeight: '40px',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          paddingLeft: '0.75rem',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: '#6c757d',
                          fontSize: '0.875rem',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: '0.875rem',
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      menuPortalTarget={document.body}
                    />
                  </div>


                  <div className="row g-3 mb-3">
                    <div className="col-md-8">
                      <div className="form-floating">
                        <input type="text" className="form-control bg-light border-0 text-dark" id="plat" name="platform"
                          placeholder="Plat" value={formData.platform} onChange={handleChange} required />
                        <label htmlFor="plat" className="text-dark">Platform</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-floating">
                        <select className="form-select bg-light border-0 text-dark" id="mode" name="mode" value={formData.mode} onChange={handleChange} required>
                          <option value="">Select</option>
                          <option value="online">Online</option>
                          <option value="offline">Offline</option>
                        </select>
                        <label htmlFor="mode" className="text-dark">Mode</label>
                      </div>
                    </div>
                  </div>

                  {/* Credits & Grade Scale Row */}
                  <div className="row g-3 mb-3">
                    {/* Credit Weightage */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">CREDIT WEIGHTAGE</label>
                      <select
                        className="form-select bg-light border-0 text-dark py-2"
                        name="credits"
                        value={formData.credits}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Credits</option>
                        {creditOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    {/* Grade Point Scale */}
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">GRADE SCALE</label>
                      <select
                        className="form-select bg-light border-0 text-dark py-2"
                        name="gradeScale"
                        value={formData.gradeScale}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Scale</option>
                        <option value="10">10 Point Scale</option>
                        <option value="7">7 Point Scale</option>
                        <option value="5">5 Point Scale</option>
                        <option value="4">4 Point Scale</option>
                        <option value="100">Percentage (100)</option>
                      </select>
                    </div>
                  </div>

                  {/* ABC Availability Toggle */}
                  <div className="bg-light p-3 rounded-3 mb-3 border border-light">
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" role="switch" id="abcSwitch"
                        name="abcAvailable" checked={formData.abcAvailable} onChange={handleChange} />
                      <label className="form-check-label fw-semibold small text-dark" htmlFor="abcSwitch">
                        Is credits available in ABC?
                      </label>
                    </div>

                    {/* Collapsible/Disable-able Section */}
                    <div className={`mt-3 transition-all ${!formData.abcAvailable ? 'opacity-50' : ''}`}>
                      <div className="row g-2">
                        <div className="col-4">
                          <select className="form-select form-select-sm bg-white border-0 text-dark" name="grade"
                            value={formData.grade} onChange={handleChange} disabled={!formData.abcAvailable} required={formData.abcAvailable}>
                            <option value="">Grade</option>
                            {grades.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div className="col-4">
                          <input type="text" className="form-control form-control-sm bg-white border-0 text-dark" placeholder="Grade Point"
                            name="gradePoint" value={formData.gradePoint} onChange={handleChange} disabled={!formData.abcAvailable} required={formData.abcAvailable} />
                        </div>
                        <div className="col-4">
                          <input type="number" className="form-control form-control-sm bg-white border-0 text-dark" placeholder="Percentage"
                            name="percentage" value={formData.percentage} onChange={handleChange} disabled={!formData.abcAvailable} required={formData.abcAvailable} min="0" max="100" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* File Upload Area */}
                  <div className="mb-4">
                    <label className="small text-dark fw-bold mb-2 d-block">CERTIFICATE UPLOAD</label>

                    {documents.length === 0 ? (
                      <div
                        className="position-relative p-4 text-center bg-light border border-dashed rounded-3"
                        style={{ borderStyle: 'dashed', borderColor: '#ccc' }}
                      >
                        <input
                          type="file"
                          className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                          accept=".pdf,.jpg,.png"
                          multiple
                          onChange={handleFileChange}
                        />
                        <div className="mb-2">
                          <i className="fas fa-cloud-upload-alt fa-2x text-primary"></i>
                        </div>
                        <p className="small mb-0 text-dark">
                          Tap to upload documents
                        </p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {documents.map((doc, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between p-3 bg-white rounded-3 border"
                          >
                            <div className="d-flex align-items-center gap-3 overflow-hidden">
                              <div className="bg-light p-2 rounded text-dark">
                                <i className="fas fa-file-alt fa-lg"></i>
                              </div>

                              <div className="text-truncate">
                                <p
                                  className="mb-0 fw-semibold text-dark text-truncate"
                                  style={{ maxWidth: '200px' }}
                                >
                                  {doc.name}
                                </p>
                                <p className="mb-0 text-muted small">
                                  {doc.size}
                                </p>
                              </div>
                            </div>

                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                onClick={() => setPreviewDoc(doc)}
                                className="btn btn-sm btn-outline-secondary"
                                title="Preview"
                              >
                                <i className="fas fa-eye"></i>
                              </button>

                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="btn btn-sm btn-outline-danger"
                                title="Remove"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit Button - Disabled if ABC is false */}
                  <button
                    type="submit"
                    disabled={!formData.abcAvailable || isLoading}
                    className="btn btn-primary w-100 py-3 rounded-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                  >
                    {isLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                    ) : (
                      <>Submit Application <i className="fas fa-paper-plane"></i></>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* SECTION 2: LIST (Full Width) */}
          <div className="col-12">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-white border-bottom pt-4 px-4 pb-3 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-0 text-dark">Submission History</h5>
                <span className="badge bg-light text-dark border">{applications.length} Records</span>
              </div>

              {/* DESKTOP VIEW: TABLE */}
              <div className="d-none d-md-block card-body p-0">
                {applications.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted">No credit applications found.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4 py-3 small text-muted text-uppercase">Course</th>
                          <th className="py-3 small text-muted text-uppercase">Credits/Mode</th>
                          <th className="py-3 small text-muted text-uppercase">Grades</th>
                          <th className="py-3 small text-muted text-uppercase">Status</th>
                          <th className="pe-4 py-3 small text-muted text-uppercase">Documents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map(app => (
                          <tr key={app.application_id}>
                            <td className="ps-4">
                              <div className="fw-bold text-dark">{app.course_name}</div>
                              <small className="text-muted">{app.platform} • Lvl {app.course_level}</small>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="fw-semibold text-dark">{app.acquired_credits} Credits</span>
                                <span className="small text-muted text-capitalize">{app.mode}</span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border fw-normal">{app.grade} ({app.percentage}%)</span>

                            </td>
                            <td>
                              <span className={`badge rounded-pill ${getStatusBadge(app.application_label)} border d-inline-flex align-items-center gap-1 px-2 py-1`}>
                                {app.application_label}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {app.documents.length > 0 ? (
                                  app.documents.map((doc, index) => (
                                    <span
                                      className="badge bg-light text-dark border fw-normal text-truncate"
                                      style={{ maxWidth: '100px' }}
                                      title={doc.file_name}
                                    >
                                      {doc.file_name}
                                    </span>

                                  ))
                                ) : (
                                  <span className="text-muted small">No documents</span>
                                )}
                              </div>
                            </td>


                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* MOBILE VIEW: CARDS */}
              <div className="d-md-none card-body p-3 bg-light">
                {applications.length === 0 ? (
                  <div className="text-center py-5 text-muted">No applications found.</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {applications.map(app => (
                      <div key={app.application_id} className="card border-0 shadow-sm p-3 rounded-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="fw-bold text-dark mb-0">{app.course_name}</h6>
                            <small className="text-muted">{app.platform}</small>
                          </div>
                          <span className={`badge rounded-pill ${getStatusBadge(app.application_label)}`}>{app.application_label}</span>
                        </div>

                        <div className="d-flex gap-2 mb-3">
                          <span className="badge bg-light text-dark border fw-normal">{app.acquired_credits} Credits</span>
                          <span className="badge bg-light text-dark border fw-normal text-capitalize">{app.mode}</span>
                          {app.ABC_status == 1 && <span className="badge bg-light text-dark border fw-normal">Grade: {app.grade}</span>}
                        </div>

                        <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-1">
                          <small className="text-muted"><i className="fas fa-calendar-alt me-1"></i>{formatDate(app.created_at)}</small>
                          {app.documentName && (
                            <button className="btn btn-sm text-primary p-0" onClick={() => toast.info(`Viewing ${app.documentName}`)}>
                              <i className="fas fa-file-pdf me-1"></i> View Doc
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* DOCUMENT PREVIEW MODAL */}
      {previewDoc && (
        <div
          className="modal fade show d-block d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content shadow-lg border-0">

              <div className="modal-header border-bottom">
                <h6 className="modal-title fw-bold text-truncate">
                  <i className="fas fa-file-alt me-2 text-primary"></i>
                  {previewDoc.name}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewDoc(null)}
                ></button>
              </div>

              <div className="modal-body bg-light p-0 text-center">
                {previewDoc.file.type.startsWith("image/") ? (
                  <img
                    src={previewDoc.url}
                    alt="preview"
                    className="img-fluid"
                    style={{ maxHeight: '70vh' }}
                  />
                ) : (
                  <iframe
                    src={`${previewDoc.url}#toolbar=0`}
                    className="w-100"
                    style={{ height: '60vh', border: 'none' }}
                    title="pdf"
                  />
                )}
              </div>

            </div>
          </div>
        </div>
      )}

    </StudentTemplate>
  );
};

export default AcademicCredit;